import { useState } from "react";
import { Loader2, Calculator, Zap, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export const ReconcileTab = ({
  reconcileLoading, reconcileData, availableForRecon,
  allocating, handleAutoRecon, handleDebugBalance,
  handleInvoiceDoubleClick, handleWriteOff, formatDate,
  loyaltyBalance, onUseLoyalty, applyingLoyalty,
}) => {
  const [showLoyaltyForm, setShowLoyaltyForm] = useState(false);
  const [partialAmount, setPartialAmount] = useState("");

  const handleApplyPartial = () => {
    const amt = parseFloat(partialAmount);
    if (!amt || amt <= 0) return;
    onUseLoyalty(amt);
    setShowLoyaltyForm(false);
    setPartialAmount("");
  };

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-purple-700 to-purple-600 text-white p-4 rounded">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Calculator className="h-6 w-6" />
              <h2 className="text-2xl font-bold">ACCOUNT RECONCILIATION</h2>
            </div>
            <p className="text-purple-200 text-sm mt-1">
              <span className="inline-block w-3 h-3 bg-black rounded mr-1"></span> Unpaid invoices |
              <span className="inline-block w-3 h-3 bg-orange-500 rounded mx-1"></span> Paid invoices
            </p>
          </div>
          <div className="text-right flex items-center gap-4">
            <div className="flex flex-col gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="bg-green-500 hover:bg-green-600 text-white border-green-600"
                onClick={handleAutoRecon}
                disabled={allocating || availableForRecon <= 0}
                data-testid="auto-recon-btn"
              >
                {allocating ? (
                  <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Allocating...</>
                ) : (
                  <><Zap className="h-4 w-4 mr-1" /> Auto Recon</>
                )}
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="bg-white/10 hover:bg-white/20 text-white border-white/30"
                onClick={handleDebugBalance}
              >
                Debug Balance
              </Button>
              {loyaltyBalance > 0 && !showLoyaltyForm && (
                <div className="flex flex-col gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="bg-purple-500 hover:bg-purple-400 text-white border-purple-400"
                    onClick={() => onUseLoyalty(loyaltyBalance)}
                    disabled={applyingLoyalty}
                    data-testid="use-loyalty-btn"
                  >
                    {applyingLoyalty ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Gift className="h-4 w-4 mr-1" />
                    )}
                    Use Loyalty R{loyaltyBalance.toFixed(2)}
                  </Button>
                  <button
                    type="button"
                    className="text-[11px] text-purple-200 hover:text-white underline"
                    onClick={() => { setShowLoyaltyForm(true); setPartialAmount(""); }}
                    data-testid="use-loyalty-partial-toggle"
                  >
                    Use only part of it
                  </button>
                </div>
              )}
              {loyaltyBalance > 0 && showLoyaltyForm && (
                <div className="flex flex-col gap-1 bg-purple-800/40 rounded p-2">
                  <div className="text-[11px] text-purple-100">
                    Available: R{loyaltyBalance.toFixed(2)}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-white text-sm">R</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max={loyaltyBalance}
                      placeholder="Amount"
                      value={partialAmount}
                      onChange={(e) => setPartialAmount(e.target.value)}
                      className="h-7 w-24 text-black"
                      data-testid="loyalty-partial-amount"
                      autoFocus
                    />
                    <Button
                      size="sm"
                      className="h-7 bg-purple-500 hover:bg-purple-400 text-white border-purple-400"
                      onClick={handleApplyPartial}
                      disabled={
                        applyingLoyalty ||
                        !partialAmount ||
                        parseFloat(partialAmount) <= 0 ||
                        parseFloat(partialAmount) > loyaltyBalance
                      }
                      data-testid="apply-loyalty-partial-btn"
                    >
                      {applyingLoyalty ? <Loader2 className="h-3 w-3 animate-spin" /> : "Apply"}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-white hover:bg-white/10"
                      onClick={() => { setShowLoyaltyForm(false); setPartialAmount(""); }}
                    >
                      ✕
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div>
              <p className="text-sm text-purple-200">Amount Available for Recon</p>
              <p className="text-3xl font-bold font-mono">R {availableForRecon.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      <p className="text-sm text-gray-600 italic">
        Double-click on an unpaid invoice (black) to allocate available funds
      </p>

      {/* Reconcile Table */}
      <div className="border rounded overflow-auto max-h-[400px]">
        {reconcileLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <Table>
            <TableHeader className="sticky top-0 bg-gray-200">
              <TableRow>
                <TableHead className="font-bold">DocType</TableHead>
                <TableHead className="font-bold">Date</TableHead>
                <TableHead className="font-bold">InvNo</TableHead>
                <TableHead className="font-bold text-right">Invoice Total</TableHead>
                <TableHead className="font-bold text-right">Amount Paid</TableHead>
                <TableHead className="font-bold text-right">Balance</TableHead>
                <TableHead className="font-bold">Description</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reconcileData.invoices?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No invoices found
                  </TableCell>
                </TableRow>
              ) : (
                reconcileData.invoices?.map((inv, idx) => {
                  const isPurchase = inv.doc_type === 'PURCHASE';
                  const isDebitNote = inv.doc_type === 'DEBIT NOTE';
                  const isPaid = isPurchase && inv.inv_amt_paid >= inv.inv_total;
                  const hasPartialPayment = (isPurchase || isDebitNote) && inv.inv_amt_paid > 0 && inv.inv_amt_paid < inv.inv_total;
                  const isAllocatable = (isPurchase || isDebitNote) && inv.balance > 0;
                  const isPayment = inv.doc_type === 'PAYMENT' || inv.doc_type === 'CREDIT NOTE';
                  const isRefund = inv.doc_type === 'REFUND';
                  
                  return (
                    <TableRow 
                      key={idx} 
                      className={`
                        ${isPaid ? 'bg-orange-100 text-orange-800' : ''}
                        ${hasPartialPayment ? 'bg-yellow-50' : ''}
                        ${isAllocatable ? 'bg-white cursor-pointer hover:bg-blue-50' : ''}
                        ${isPayment ? 'bg-green-50' : ''}
                        ${isRefund ? 'bg-blue-50' : ''}
                        ${isDebitNote && inv.balance > 0 ? 'border-l-4 border-l-red-400' : ''}
                      `}
                      onDoubleClick={() => isAllocatable && handleInvoiceDoubleClick(inv)}
                      title={isAllocatable ? "Double-click to allocate payment" : ""}
                    >
                      <TableCell>
                        <span className={isPayment ? 'text-green-700' : isRefund ? 'text-blue-700' : ''}>
                          {inv.doc_type}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(inv.date)}</TableCell>
                      <TableCell className="font-mono">{inv.inv_no}</TableCell>
                      <TableCell className="text-right font-mono">
                        {inv.inv_total?.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-green-600">
                        {inv.inv_amt_paid?.toFixed(2)}
                      </TableCell>
                      <TableCell className={`text-right font-mono ${inv.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {inv.balance?.toFixed(2)}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate" title={inv.description}>
                        <div className="flex items-center justify-between gap-1">
                          <span className="truncate">{inv.description}</span>
                          {isAllocatable && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 shrink-0"
                              onClick={(e) => { e.stopPropagation(); handleWriteOff(inv); }}
                              title="Write off remaining balance"
                              data-testid={`write-off-${inv.inv_no}`}
                            >
                              Write Off
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};
